import type { LeftNavComponent } from '../../../common-components/left-nav/left-nav.component';
import { appleStep } from '../../ios-app.util';
import { COUNTRY_CHOICES, type CountryChoice, rejectedPauseShown } from '../../app-store-listing.util';
import { normaliseColour } from '../../../shared/system-bars/system-bars.util';
import { searchTimeZones } from '../../../shared/time/project-clock';
import { Prefilled, Prepared, SaveOutcome } from './confirm-lane';
import type { ScreenAction, ScreenActionAnswer } from './fast-lane';
import type { ConfirmStep } from './other-confirm';

/** The commits the builder presses on the Settings cards. Their waits end when Settings closes. */
export const SETTINGS_PRESS_COMMITS = ['unpublish', 'rebuild', 'set-time-zone', 'connect-apple', 'set-runtime-test-password'] as const;

/** Fields that come from the App Store listing: until it has loaded the form behind them is blank, which isn't what the project holds. */
const FROM_LISTING = [
  'listingName', 'subtitle', 'description', 'keywords', 'supportUrl', 'marketingUrl', 'promotionalText', 'copyright', 'primaryCategory',
  'secondaryCategory', 'noObjectionableContent', 'usesThirdPartyContent', 'traderStatusConfirmed', 'countries', 'distribution', 'privacyText', 'rejectionNotes',
];

/**
 * What Settings holds for read_screen. A value that hasn't been read for this project is left out so it reads as not read,
 * rather than as an empty answer. Each object below names its fields so a new secret on a server response can't ride along.
 */
export function settingsHolds(nav: LeftNavComponent, base: Record<string, unknown>): Record<string, unknown> {
  const apple = nav.appleAccount;
  const listing = nav.listingStatus;
  const holds: Record<string, unknown> = {
    ...base,
    selectedTenant: nav.selectedTenantId,
    publishUrl: nav.publishUrl,
    devUrl: nav.devUrl,
    appBars: nav.appBars,
    developerMode: !!nav.developerMode?.on,
    worksOffline: nav.worksOffline.on(),
    devApk: nav.devApk ? { status: nav.devApk.status, versionCode: nav.devApk.versionCode, error: nav.devApk.error } : null,
    liveApk: nav.liveApk ? { status: nav.liveApk.status, error: nav.liveApk.error } : null,
    apple: { step: appleStep(apple), connected: !!apple?.connected, appleAppId: apple?.appleAppId ?? null, bundleId: apple?.bundleId ?? null, message: apple?.message ?? null },
    iphoneBuild: nav.iosBuild ? { status: nav.iosBuild.status, buildNumber: nav.iosBuild.buildNumber, publishedVersion: nav.iosBuild.publishedVersion, error: nav.iosBuild.error ?? null } : null,
    iphonePublicLink: { link: nav.iosPublicLink, enabled: nav.iosPublicLinkEnabled },
    appStoreReminders: { level: nav.iosNudge.level, day: nav.iosNudge.day, off: nav.iosNudgeSwitch?.off ?? null },
    testLoginRoles: nav.testLogins.map((t) => ({ roleId: t.roleId, daysUntilRotation: t.daysUntilRotation })),
  };
  if (!nav.versionsKnown()) delete holds['versions'];
  // Read from the server only when the builder reveals it, so until then blank doesn't mean there is none.
  if (!nav.runtimeLoginShown) { delete holds['runtimeLoginEmail']; delete holds['runtimeLoginPassword']; }
  // Read only for an editor of a project with a preview, so otherwise an empty list says nothing.
  if (!nav.canEditProject || !nav.devUrl) delete holds['testLogins'];
  // Nor does an empty list after a failed read.
  if (nav.testLoginsLoadFailed) { delete holds['testLogins']; delete holds['testLoginRoles']; }
  // Every account has a code, so a blank one is a load that hasn't finished or that failed, not a builder without one.
  if (!holds['builderCode']) delete holds['builderCode'];
  // These boxes come back empty even when the project holds one, because a secret is never sent to the editor.
  for (const box of ['demoPassword', 'teamId', 'issuerId', 'keyId', 'p8', 'runtimeNewPassword']) if (!holds[box]) delete holds[box];
  if (!listing) {
    for (const field of FROM_LISTING) delete holds[field];
    return holds;
  }
  return {
    ...holds,
    listingErrors: nav.listingErrors,
    checklist: listing.checklist,
    canSubmit: listing.canSubmit,
    submission: listing.submission,
    privacyChoice: nav.privacyChoice(),
    privacyApproved: nav.privacyApproved(),
    screenshots: (listing.listing?.screenshots ?? []).map((s) => s.path),
  };
}

const NOT_OPEN = 'Settings isn\'t open. Nothing was changed; call navigate first.';
type Reach = 'any' | 'editor' | 'owner' | 'published-owner';

/** The same conditions the cards show under, so an action can't reach what the builder couldn't see. */
function outOfReach(nav: LeftNavComponent, reach: Reach): string | null {
  if (!nav.showSettings) return NOT_OPEN;
  if (nav.settingsLoading) return 'Settings is still loading. Nothing was changed; try again in a moment.';
  if (reach === 'editor' && !nav.canEditProject) return 'Only an owner or editor of this project can do that. Nothing was changed.';
  if ((reach === 'owner' || reach === 'published-owner') && !nav.isProjectOwner) return 'Only the project owner can do that. Nothing was changed.';
  if (reach === 'published-owner' && !nav.publishUrl) return 'This project isn\'t published yet. Nothing was changed.';
  return null;
}

const refused = (why: string) => ({ refused: why });
const unchanged = (result: Record<string, unknown>): Prefilled & Prepared => ({ answer: { outcome: 'done', changed: [], result: { outcome: 'unchanged', ...result } } });
const failed = (reason: string): SaveOutcome => ({ outcome: 'failed', reason });

function str(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function needListing(nav: LeftNavComponent): string | null {
  if (!nav.listingStatus) return 'The App Store section isn\'t loaded for this project yet: it needs the Apple app record first. Nothing was changed.';
  return null;
}

function fast(nav: LeftNavComponent, reach: Reach, act: (values: Record<string, unknown>) => Promise<ScreenActionAnswer>): ScreenAction {
  return async (values) => {
    const why = outOfReach(nav, reach);
    return why ? refused(why) : act(values);
  };
}

/** Fast-lane actions on the Settings cards. Each runs what the card's own control runs. */
export function settingsFastActions(nav: LeftNavComponent): Record<string, ScreenAction> {
  return {
    'pick-tenant': fast(nav, 'owner', async (v) => {
      const tenant = str(v['tenant'])?.trim() ?? '';
      if (!tenant) { nav.selectTenant(null); return { done: 'Showing the Default (Editor) tenant.' }; }
      const found = nav.tenants.find((t) => t.id === tenant || t.name === tenant);
      if (!found) return refused(`There is no tenant "${tenant}". read_screen lists them.`);
      nav.selectTenant(found.id);
      return { done: `Showing the ${found.name} tenant.`, result: { tenant: found.id } };
    }),

    // The one way Claude reaches this save: everything else that touches the name leaves the box's blur alone.
    'set-mobile-app-name': fast(nav, 'editor', async (v) => {
      const name = str(v['mobileAppName'])?.trim();
      if (!name) return refused('Give the app a name. Nothing was changed.');
      if (name.length > 26) return refused('The mobile app name can be at most 26 characters. Nothing was changed.');
      const before = nav.mobileAppName;
      nav.mobileAppName = name;
      try {
        await nav.saveMobileAppName();
      } catch (err) {
        nav.mobileAppName = before;
        throw err;
      }
      return { done: `The mobile app name is now ${name}. It takes effect the next time the app is built.`, result: { mobileAppName: name } };
    }),

    'set-app-bars': fast(nav, 'editor', async (v) => {
      const bar = v['bar'];
      if (bar !== 'status' && bar !== 'navigation') return refused('bar must be "status" or "navigation". Nothing was changed.');
      const raw = str(v['colour'])?.trim();
      const colour = raw ? normaliseColour(raw) : null;
      if (raw && !colour) return refused(`"${raw}" isn't a colour; use a hex colour like #1a2b3c. Nothing was changed.`);
      if (!colour && bar === 'status') return refused('The status bar needs a colour. Only the navigation bar can follow another bar. Nothing was changed.');
      await nav.setAppBar(bar, colour);
      if (nav.appBarError) throw new Error(nav.appBarError);
      return { done: colour ? `The ${bar} bar is now ${colour}.` : 'The navigation bar now uses the status bar colour.' };
    }),

    'save-app-review-details': fast(nav, 'owner', async (v) => {
      if (!nav.appleAccount?.appleAppId) return refused('The review details need the Apple app record first. Nothing was changed.');
      const fields = ['contactFirstName', 'contactLastName', 'contactEmail', 'contactPhone', 'demoUsername', 'demoPassword', 'notes'] as const;
      const form = { ...nav.reviewForm };
      for (const field of fields) { const value = str(v[field]); if (value !== undefined) form[field] = value; }
      // Apple gets the password with every save and the server never returns it, so the card always asks for it.
      if (!form.demoPassword) return refused('The reviewer\'s password is needed on every save, and isn\'t in the form. Nothing was changed.');
      nav.reviewForm = form;
      await nav.saveReview();
      if (!nav.reviewSaved) throw new Error(nav.reviewError ?? 'The review details weren\'t saved.');
      return { done: 'The app review details are saved.' };
    }),

    'save-app-store-listing': fast(nav, 'owner', async (v) => {
      const notLoaded = needListing(nav);
      if (notLoaded) return refused(notLoaded);
      const countries = v['countries'];
      if (countries !== undefined && !COUNTRY_CHOICES.some((c) => c.value === countries)) return refused(`countries must be one of ${COUNTRY_CHOICES.map((c) => c.value).join(', ')}. Nothing was changed.`);
      if (countries !== undefined && nav.countryOptionLocked(countries as CountryChoice)) return refused(`${nav.countriesLockedMessage} Nothing was changed.`);
      const distribution = v['distribution'];
      if (distribution !== undefined && distribution !== 'public' && distribution !== 'unlisted') return refused('distribution must be "public" or "unlisted". Nothing was changed.');
      const before = nav.listingForm;
      const textFields = ['subtitle', 'description', 'keywords', 'supportUrl', 'marketingUrl', 'promotionalText', 'copyright', 'primaryCategory', 'secondaryCategory'] as const;
      if (typeof v['listingName'] === 'string') nav.setListingField('name', v['listingName']);
      for (const field of textFields) { const value = str(v[field]); if (value !== undefined) nav.setListingField(field, value); }
      for (const field of ['noObjectionableContent', 'usesThirdPartyContent', 'traderStatusConfirmed'] as const) {
        if (typeof v[field] === 'boolean') nav.setListingFlag(field, v[field] as boolean);
      }
      if (countries !== undefined) nav.setListingChoice({ countries: countries as CountryChoice });
      if (distribution !== undefined) nav.setListingChoice({ distribution: distribution as 'public' | 'unlisted' });
      await nav.saveListing();
      if (nav.listingSaved) return { done: 'The App Store listing draft is saved.' };
      const why = nav.listingErrors.join(' ') || 'A save or a check is already running.';
      // The card's error list stays for the builder; the fill goes, since the answer says nothing changed.
      nav.listingForm = before;
      return refused(`${why} Nothing was changed.`);
    }),

    'save-apple-rejection-notes': fast(nav, 'owner', async (v) => {
      if (!rejectedPauseShown(nav.listingStatus?.submission)) return refused('Apple\'s rejection notes are only saved for a rejected version, and this one isn\'t. Nothing was changed.');
      const before = nav.rejectionNotesText;
      nav.setRejectionNotesText(str(v['rejectionNotes']) ?? '');
      await nav.saveRejectionNotes();
      if (nav.rejectionNotesSaved) return { done: 'Apple\'s rejection notes are saved.' };
      const why = nav.rejectionNotesError ?? 'Another save is already running.';
      nav.rejectionNotesText = before;
      return refused(`${why} Nothing was changed.`);
    }),

    // The server keeps only Zavo's own draft and the approved text, so a written policy waits in the box for Approve.
    'save-privacy-policy-draft': fast(nav, 'owner', async (v) => {
      const notLoaded = needListing(nav);
      if (notLoaded) return refused(notLoaded);
      if (nav.privacyChoice() !== 'zavo') return refused('This project uses its own privacy link, so there is no Zavo policy to draft. Nothing was changed.');
      const text = str(v['privacyText'])?.trim();
      if (text) {
        if (nav.privacyApproved() && !nav.privacyEditing) nav.editPrivacyPolicy();
        nav.privacyText = text;
        return { done: 'The policy is in the box on the iPhone card. It isn\'t saved until the builder approves it, so don\'t say it is.' };
      }
      await nav.draftPrivacyPolicy(true);
      if (nav.privacyError) throw new Error(nav.privacyError);
      return { done: 'Zavo\'s draft is in the box on the iPhone card, waiting for the builder to approve it.' };
    }),

    'set-developer-mode': fast(nav, 'editor', async (v) => {
      if (!nav.canSetDeveloperMode) return refused('Developer mode can only be switched by the Zavo team, in the Dev editor. Nothing was changed.');
      if (typeof v['enabled'] !== 'boolean') return refused('enabled must be true or false. Nothing was changed.');
      if (!!nav.developerMode?.on === v['enabled']) return { done: `Developer mode was already ${v['enabled'] ? 'on' : 'off'}.` };
      await nav.toggleDeveloperMode();
      if (nav.developerModeError) throw new Error(nav.developerModeError);
      return { done: `Developer mode is ${nav.developerMode?.on ? 'on' : 'off'}.`, result: { developerMode: !!nav.developerMode?.on } };
    }),

    'tick-website-checklist': fast(nav, 'owner', async (v) => {
      const notLoaded = needListing(nav);
      if (notLoaded) return refused(notLoaded);
      const item = nav.checklistItem(str(v['item']) ?? '');
      if (!item) return refused(`There is no checklist item "${String(v['item'])}". read_screen lists them.`);
      if (!item.websiteOnly) return refused(`"${item.label}" is checked by Zavo, not ticked by hand. Nothing was changed.`);
      if (typeof v['ticked'] !== 'boolean') return refused('ticked must be true or false. Nothing was changed.');
      if (nav.stepBusy) return refused('Another checklist step is still saving. Nothing was changed; try again in a moment.');
      await nav.tickWebsiteStep(item, { checked: v['ticked'] });
      if (nav.checklistError) throw new Error(nav.checklistError);
      return { done: `"${item.label}" is ${v['ticked'] ? 'ticked' : 'unticked'}.` };
    }),

    'build-dev-apk': fast(nav, 'editor', async () => {
      if (!nav.devUrl) return refused('This project has no Preview yet. Nothing was started.');
      if (nav.devApk?.status === 'queued' || nav.devApk?.status === 'running') return refused('A Dev APK is already building. Nothing new was started.');
      await nav.buildDevApk();
      if (nav.devApk?.status === 'failed') throw new Error(nav.devApk.error ?? 'The Dev APK could not start.');
      return { done: 'The Dev APK build has started. read_screen shows how it goes.', result: { status: nav.devApk?.status } };
    }),

    'refresh-apple-status': fast(nav, 'owner', async () => {
      if (!nav.appleAccount?.connected) return refused('No Apple account is connected yet. Nothing was refreshed.');
      if (nav.appleBusy) return refused('Apple is already being checked. Try again in a moment.');
      await nav.refreshApple();
      if (nav.appleError) throw new Error(nav.appleError);
      return { done: 'Apple\'s status is up to date.', result: { step: appleStep(nav.appleAccount) } };
    }),
  };
}

function press(nav: LeftNavComponent, reach: Reach, _label: (nav: LeftNavComponent) => string, fill: (values: Record<string, unknown>) => Promise<Prefilled> | Prefilled): ConfirmStep {
  return {
    press: async (values) => {
      const why = outOfReach(nav, reach);
      if (why) return refused(why);
      // Was (2026-10-30), before the highlight overlay brought every marked button on screen itself:
      // if (!('refused' in filled) && !('answer' in filled)) nav.revealCommitButton(filled.button ?? label(nav));
      return await fill(values);
    },
  };
}

/** Why the iPhone switches can't be read. The status is only fetched for a connected account, so waiting for it with none would never end. */
function iphoneStatusProblem(nav: LeftNavComponent): string | null {
  const loading = 'The iPhone status is still loading. Nothing was changed; try again in a moment.';
  if (nav.iphoneLoading) return loading;
  if (nav.iphoneLoadFailed) return 'Couldn\'t read the iPhone status. Nothing was changed; the builder can press Retry on the iPhone app card.';
  // Was (2026-10-30), when no account also covered not read yet: if (!nav.appleAccount?.connected) return 'This switch comes with…';
  // Only an editor's open starts this read, so anyone else must not be told to wait for it.
  if (!nav.appleAccount?.connected) return nav.canEditProject && !nav.iphoneAccountRead ? loading : 'This switch comes with a connected Apple account, and this project has none. Nothing was changed.';
  return nav.iosStatusLoaded ? null : loading;
}

type Card = Exclude<Prepared, { refused: string } | { answer: unknown }>;

/** A card stays up while the builder reads it. The click must act on the Settings and the project it was worded for. */
function card(nav: LeftNavComponent, reach: Reach, prepare: (values: Record<string, unknown>) => Promise<Prepared> | Prepared): ConfirmStep {
  return {
    overlay: async (values) => {
      const why = outOfReach(nav, reach);
      if (why) return refused(why);
      const prepared = await prepare(values);
      if ('refused' in prepared || 'answer' in prepared) return prepared;
      const project = nav.settingsProjectId();
      const { run } = prepared as Card;
      return {
        ...prepared,
        run: async (): Promise<SaveOutcome> => {
          if (!nav.showSettings) return { outcome: 'closed', because: 'the builder closed Settings' };
          if (nav.settingsProjectId() !== project) return { outcome: 'closed', because: 'the builder moved to another project' };
          const noLonger = outOfReach(nav, reach);
          if (noLonger) return failed(noLonger);
          const outcome = await run();
          // The component drops an answer about the project that was left, so its fields now describe the next one.
          if (nav.settingsProjectId() !== project) return { outcome: 'closed', because: 'the builder moved to another project before the answer came back', ran: true };
          return outcome;
        },
      };
    },
  };
}

/** Confirm-lane steps on the Settings cards: a form the builder presses, or a card the builder clicks. */
export function settingsConfirmSteps(nav: LeftNavComponent): Record<string, ConfirmStep> {
  return {
    publish: card(nav, 'owner', () => {
      if (nav.publishLoading) return refused('A publish is already running. Nothing new was started.');
      const name = nav.selectedClientName || 'this project';
      return {
        says: `Publish ${name} now? Everyone using the Live app gets this version.`,
        confirmLabel: 'Publish',
        run: async () => {
          // Every handler below returns quietly when it is busy, which would read as a success from here.
          if (nav.publishLoading) return failed('A publish is already running. Nothing new was started.');
          await nav.publish();
          if (nav.publishError) return failed(nav.publishError);
          return { outcome: 'saved', saved: { publishVersion: nav.publishVersion, publishUrl: nav.publishUrl, note: nav.publishNote } };
        },
      };
    }),

    'revert-version': card(nav, 'published-owner', async (v) => {
      const version = str(v['version'])?.trim();
      if (!version) return refused('Say which version. read_screen lists them. Nothing was changed.');
      // Always afresh: a list read earlier may belong to the project before a switch.
      await nav.loadVersions();
      if (version === nav.publishVersion) return unchanged({ publishVersion: version });
      if (!nav.versions.some((x) => x.version === version)) return refused(`There is no version ${version}. Nothing was changed.`);
      if (!nav.canRevert(version)) return refused(`Version ${version} is on an older major line, so it can't be reverted to. Nothing was changed.`);
      return {
        says: `Put version ${version} back live? Everyone using the Live app goes back to it.`,
        confirmLabel: 'Revert',
        run: async () => {
          if (nav.reverting) return failed('A revert is already running. Nothing new was started.');
          await nav.revert(version);
          if (nav.publishError) return failed(nav.publishError);
          return { outcome: 'saved', saved: { publishVersion: nav.publishVersion, note: nav.publishNote } };
        },
      };
    }),

    unpublish: press(nav, 'published-owner', () => 'Unpublish', (v) => {
      const name = str(v['unpublishName'])?.trim() ?? '';
      if (nav.unpublishing) return refused('An unpublish is already running.');
      if (nav.unpublishOpen && nav.unpublishName && nav.unpublishName !== name) return refused('The builder has already typed in the Unpublish form. Nothing was changed.');
      nav.openUnpublish();
      // The box otherwise appears only after the server asks for the name, and the server ignores it when nobody uses the app.
      if (name) { nav.unpublishNeedsName = true; nav.unpublishName = name; }
      return {};
    }),

    rebuild: press(nav, 'owner', () => 'Confirm', (v) => {
      if (!nav.canSetDeveloperMode) return refused('A rebuild can only be run by the Zavo team, in the Dev editor. Nothing was changed.');
      if (!nav.appleAccount?.connected) return refused('No Apple account is connected. Nothing was changed.');
      if (nav.rebuilding) return refused('A rebuild is already starting.');
      const name = str(v['rebuildName'])?.trim() ?? '';
      const note = str(v['rebuildNote'])?.trim() ?? '';
      if (nav.rebuildOpen && (nav.rebuildName || nav.rebuildNote) && (nav.rebuildName !== name || nav.rebuildNote !== note)) return refused('The builder has already typed in the Rebuild form. Nothing was changed.');
      nav.openRebuild();
      nav.rebuildName = name;
      nav.rebuildNote = note;
      return {};
    }),

    'set-works-offline': card(nav, 'editor', (v) => {
      const on = v['enabled'];
      if (typeof on !== 'boolean') return refused('enabled must be true or false. Nothing was changed.');
      if (nav.worksOffline.on() === on) return unchanged({ worksOffline: on });
      return {
        says: on ? 'Let this app work offline? It reaches a Live app when it is next published.' : 'Stop this app working offline? It reaches a Live app when it is next published.',
        confirmLabel: on ? 'Turn on' : 'Turn off',
        run: async () => {
          if (nav.worksOfflineSaving) return failed('Works offline is still saving. Nothing new was changed; try again in a moment.');
          // The builder may have flipped the card's own switch while reading this one, and the handler only toggles.
          if (nav.worksOffline.on() === on) return { outcome: 'saved', saved: { worksOffline: on } };
          await nav.toggleWorksOffline();
          if (nav.worksOfflineError) return failed(nav.worksOfflineError);
          return { outcome: 'saved', saved: { worksOffline: nav.worksOffline.on() } };
        },
      };
    }),

    // A link is switched only once Apple has made one, which is when the card shows the switch.
    'iphone-public-link': card(nav, 'owner', (v) => {
      const on = v['enabled'];
      if (typeof on !== 'boolean') return refused('enabled must be true or false. Nothing was changed.');
      const notRead = iphoneStatusProblem(nav);
      if (notRead) return refused(notRead);
      if (nav.iosPublicLinkEnabled === on) return unchanged({ iphonePublicLink: { enabled: on } });
      if (!on && !nav.iosPublicLink) return refused('There is no TestFlight link to switch off yet. Nothing was changed.');
      return {
        title: 'Switch the TestFlight link',
        says: on
          ? 'Switch the TestFlight link on? New testers can join with it again.'
          : 'Switch the TestFlight link off? New testers can\'t join. Testers who already installed keep the app.',
        confirmLabel: on ? 'Switch on' : 'Switch off',
        run: async () => {
          if (nav.iosLinkBusy) return failed('The TestFlight link is still being changed. Nothing new was changed; try again in a moment.');
          // The builder may have used the card's own link while reading this one, and the handler only toggles.
          if (nav.iosPublicLinkEnabled !== on) await nav.toggleIosLink();
          if (nav.iosPublicLinkEnabled !== on) return failed(nav.iosBuildError || 'Could not change the TestFlight link. Try again.');
          return { outcome: 'saved', saved: { iphonePublicLink: { enabled: on } } };
        },
      };
    }),

    // The same gate as the card; setIosNudge still refuses anyone outside the Zavo team on the server.
    'iphone-nudge': card(nav, 'any', (v) => {
      const on = v['enabled'];
      if (typeof on !== 'boolean') return refused('enabled must be true or false. Nothing was changed.');
      if (!nav.canSetDeveloperMode) return refused('App Store reminders are switched by the Zavo team, in the Dev editor. Nothing was changed.');
      const notRead = iphoneStatusProblem(nav);
      if (notRead) return refused(notRead);
      if (!nav.iosNudgeSwitch) return refused('Couldn\'t read the App Store reminders switch. Nothing was changed.');
      if (nav.iosNudgeSwitch.off === !on) return unchanged({ appStoreReminders: { on } });
      return {
        title: 'Switch App Store reminders',
        says: on
          ? 'Switch App Store reminders on for this project? Nothing is sent now.'
          : 'Switch App Store reminders off for this project? Nothing is sent now.',
        confirmLabel: on ? 'Switch on' : 'Switch off',
        run: async () => {
          if (nav.iosNudgeSaving) return failed('App Store reminders are still being changed. Nothing new was changed; try again in a moment.');
          if (nav.iosNudgeSwitch?.off !== !on) await nav.toggleIosNudge();
          if (nav.iosNudgeError) return failed(nav.iosNudgeError);
          if (nav.iosNudgeSwitch?.off !== !on) return failed('Could not change App Store reminders. Try again.');
          return { outcome: 'saved', saved: { appStoreReminders: { on } } };
        },
      };
    }),

    'set-time-zone': press(nav, 'owner', () => 'Change time zone', (v) => {
      const zone = str(v['timeZone'])?.trim() ?? '';
      if (!searchTimeZones(zone, 30).includes(zone)) return refused(`"${zone}" isn't a time zone name. Use one like Asia/Kolkata. Nothing was changed.`);
      if (zone === nav.projectTimeZone) return unchanged({ timeZone: zone });
      if (nav.timeZonePending && nav.timeZonePending !== zone) return refused('The builder already has a time zone change waiting. Nothing was changed.');
      nav.timeZoneQuery = zone;
      nav.pickTimeZone(zone);
      return {};
    }),

    'build-live-apk': card(nav, 'published-owner', () => {
      if (nav.liveApkBusy()) return refused('A Live APK is already building. Nothing new was started.');
      return {
        says: 'Build the Live Android app from the published version?',
        confirmLabel: 'Build',
        run: async () => {
          if (nav.liveApkBusy()) return failed('A Live APK is already building. Nothing new was started.');
          await nav.buildLiveApk();
          if (nav.liveApk?.status === 'failed') return failed(nav.liveApk.error ?? 'The Live APK could not start.');
          return { outcome: 'saved', saved: { status: nav.liveApk?.status } };
        },
      };
    }),

    'build-iphone': card(nav, 'owner', () => {
      if (nav.iosBuilding || !nav.canBuildIphone()) return refused('The iPhone app can\'t build now: it needs the project published, Apple set up, and no build already running. read_screen shows which. Nothing was started.');
      return {
        says: 'Build the iPhone app from the published version and send it to TestFlight?',
        confirmLabel: 'Build',
        run: async () => {
          if (nav.iosBuilding) return failed('An iPhone build is already starting. Nothing new was started.');
          if (!nav.canBuildIphone()) return failed('The iPhone app can\'t build now: it needs the project published, Apple set up, and no build already running. Nothing new was started.');
          await nav.buildIphone();
          if (nav.iosBuildError) return failed(nav.iosBuildError);
          return { outcome: 'saved', saved: { status: nav.iosBuild?.status ?? null } };
        },
      };
    }),

    'connect-apple': press(nav, 'owner', () => (nav.appleAccount?.connected ? 'Replace the key' : 'Connect Apple account'), (v) => {
      if (nav.appleBusy) return refused('Apple is already being contacted. Nothing was changed.');
      const fields = ['teamId', 'issuerId', 'keyId'] as const;
      for (const field of fields) {
        const given = str(v[field])?.trim();
        // The ids are the builder's until they're told otherwise; only a blank box or the same id can be written.
        if (given && nav.appleForm[field] && nav.appleForm[field] !== given) return refused('The builder has already typed different details in the Apple form. Nothing was changed.');
      }
      for (const field of fields) { const given = str(v[field])?.trim(); if (given) nav.appleForm[field] = given; }
      if (nav.appleAccount?.connected) nav.replaceKeyOpen = true;
      return {
        button: nav.appleAccount?.connected ? 'Replace the key' : 'Connect Apple account',
        also: { stillNeeded: 'The builder has to pick the .p8 key file on the card before pressing. Claude can\'t pick it.' },
      };
    }),

    'approve-privacy-policy': card(nav, 'owner', () => {
      const notLoaded = needListing(nav);
      if (notLoaded) return refused(notLoaded);
      if (nav.privacyChoice() !== 'zavo') return refused('This project uses its own privacy link, so there is no Zavo policy to approve. Nothing was changed.');
      if (!nav.privacyText.trim()) return refused('There is no policy text to approve yet. Nothing was changed.');
      return {
        says: 'Approve the privacy policy on the iPhone card? It goes out at the app\'s privacy address, and Apple gets that address.',
        confirmLabel: 'Approve',
        run: async () => {
          if (nav.privacyBusy) return failed('The privacy policy is still being changed. Nothing new was changed; try again in a moment.');
          await nav.approvePrivacyPolicy();
          if (nav.privacyError) return failed(nav.privacyError);
          return { outcome: 'saved', saved: { privacyApproved: nav.privacyApproved() } };
        },
      };
    }),

    'submit-to-app-store': card(nav, 'owner', () => {
      const notLoaded = needListing(nav);
      if (notLoaded) return refused(notLoaded);
      if (nav.submitting) return refused('A submission is already running. Nothing new was started.');
      if (!nav.listingStatus?.canSubmit) return refused('The app can\'t be submitted yet: the checklist isn\'t done. read_screen shows it. Nothing was submitted.');
      const name = nav.listingStatus.listing?.name || nav.mobileAppName || 'this app';
      return {
        says: `Submit ${name} to Apple's App Store review? Your client's business name appears as the seller.`,
        confirmLabel: 'Submit',
        run: async () => {
          if (nav.submitting) return failed('A submission is already running. Nothing new was started.');
          if (!nav.listingStatus?.canSubmit) return failed('The app can\'t be submitted now: the checklist isn\'t done. Nothing was submitted.');
          await nav.submitToAppStore(true);
          if (nav.submitError) return failed(nav.submitError);
          const submission = nav.listingStatus?.submission;
          // Apple's own refusal comes back as a success with a failed status.
          if (submission?.status === 'failed') return failed(submission.error ?? 'Apple refused the submission.');
          return { outcome: 'saved', saved: { status: submission?.status ?? null } };
        },
      };
    }),

    'remove-screenshot': card(nav, 'owner', (v) => {
      const notLoaded = needListing(nav);
      if (notLoaded) return refused(notLoaded);
      const path = str(v['screenshotId']);
      const known = () => (nav.listingStatus?.listing?.screenshots ?? []).findIndex((s) => s.path === path);
      if (known() < 0) return refused(`There is no screenshot "${String(path)}". read_screen lists them. Nothing was changed.`);
      if (nav.shotBusy) return refused('A screenshot is still being changed. Nothing was changed; try again in a moment.');
      return {
        says: `Remove screenshot ${known() + 1} from the App Store listing?`,
        confirmLabel: 'Remove',
        run: async () => {
          // Positions move as screenshots come and go, so the one the card named is found again.
          const index = known();
          if (index < 0) return { outcome: 'saved', saved: { removed: path } };
          if (nav.shotBusy) return failed('A screenshot is still being changed. Nothing new was changed; try again in a moment.');
          await nav.removeScreenshotAt(index);
          if (nav.shotError) return failed(nav.shotError);
          return { outcome: 'saved', saved: { removed: path } };
        },
      };
    }),

    'set-runtime-test-password': press(nav, 'published-owner', () => 'Update', async (v) => {
      const password = str(v['runtimeNewPassword'])?.trim() ?? '';
      if (password && password.length < 6) return refused('The password must be at least 6 characters. Nothing was changed.');
      if (nav.runtimeLoginLoading) return refused('The live app login is busy. Nothing was changed; try again in a moment.');
      // The box sits behind Show; the login it shows goes to the builder's screen and never into an answer.
      if (!nav.runtimeLoginShown) await nav.revealRuntimeLogin();
      if (!nav.runtimeLoginShown) return refused(`${nav.runtimeLoginError || 'The live app login could not be opened.'} Nothing was changed.`);
      nav.runtimePasswordPrefill = password;
      return {};
    }),

    'rotate-test-login': card(nav, 'editor', (v) => {
      const roleId = str(v['roleId']);
      if (nav.testLoginsLoadFailed) return refused('The test logins couldn\'t be read, so none can be rotated from here. Nothing was changed; ask the builder to reopen Settings.');
      if (!nav.testLogins.some((t) => t.roleId === roleId)) return refused(`There is no test login for role "${String(roleId)}". read_screen lists them. Nothing was changed.`);
      if (nav.rotatingRole) return refused('Another test login is being rotated. Nothing was changed; try again in a moment.');
      return {
        says: `Change the test login for the ${String(roleId)} role? The old password stops working.`,
        confirmLabel: 'Rotate',
        run: async () => {
          if (nav.rotatingRole) return failed('A test login is still being rotated, so this one wasn\'t. Try again in a moment.');
          await nav.rotateTestLogin(roleId as string);
          if (nav.testLoginError) return failed(nav.testLoginError);
          // The new password is on the card for the builder; only that it changed goes back.
          return { outcome: 'saved', saved: { roleId, rotated: true } };
        },
      };
    }),
  };
}
